import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const QuickViewModal = ({ product, isOpen, onClose, onAddToCart }) => {
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  if (!isOpen || !product) return null;

  const handleAddToCart = async () => {
    setIsLoading(true);
    try {
      await onAddToCart({ ...product, quantity: selectedQuantity });
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = () => {
    navigate(`/product-detail-page?id=${product.id}`);
    onClose();
  };

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="Star" size={16} className="text-warning fill-current opacity-50" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={16} className="text-border" />
      );
    }

    return stars;
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-overlay"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-overlay flex items-center justify-center p-4">
        <div className="bg-surface rounded-card shadow-modal max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="font-heading font-heading-semibold text-lg text-text-primary">
              Quick View
            </h2>
            <Button
              variant="ghost"
              onClick={onClose}
              className="p-2"
              iconName="X"
              iconSize={20}
            />
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Product Image */}
              <div className="relative">
                <Image
                  src={product.image}
                  alt={product.name}
                  className="w-full h-64 md:h-80 object-cover rounded-card"
                />
                
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col space-y-1">
                  {product.isNew && (
                    <span className="bg-accent text-accent-foreground text-xs font-data font-data-medium px-2 py-1 rounded-input">
                      New
                    </span>
                  )}
                  {product.isOnSale && (
                    <span className="bg-error text-error-foreground text-xs font-data font-data-medium px-2 py-1 rounded-input">
                      Sale
                    </span>
                  )}
                  {product.isOrganic && (
                    <span className="bg-success text-success-foreground text-xs font-data font-data-medium px-2 py-1 rounded-input">
                      Organic
                    </span>
                  )}
                </div>
              </div>

              {/* Product Details */}
              <div className="space-y-4">
                <div>
                  <h3 className="font-heading font-heading-semibold text-xl text-text-primary mb-2">
                    {product.name}
                  </h3>
                  <p className="text-text-secondary">
                    {product.unit}
                  </p>
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-0.5">
                    {renderRatingStars(product.rating)}
                  </div>
                  <span className="text-sm text-text-secondary">
                    {product.rating} ({product.reviewCount} reviews)
                  </span>
                </div>

                {/* Price */}
                <div className="flex items-center space-x-3">
                  <span className="font-data font-data-medium text-2xl text-primary">
                    ${product.price.toFixed(2)}
                  </span>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <>
                      <span className="font-data text-lg text-text-secondary line-through">
                        ${product.originalPrice.toFixed(2)}
                      </span>
                      <span className="text-sm font-data font-data-medium text-error bg-error bg-opacity-10 px-2 py-1 rounded-input">
                        {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                      </span>
                    </>
                  )}
                </div>

                {/* Description */}
                {product.description && (
                  <div>
                    <h4 className="font-body-semibold text-text-primary mb-2">Description</h4>
                    <p className="text-sm text-text-secondary line-clamp-3">
                      {product.description}
                    </p>
                  </div>
                )}

                {/* Stock Status */}
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={product.stock > 0 ? "CheckCircle" : "XCircle"} 
                    size={16} 
                    className={product.stock > 0 ? "text-success" : "text-error"}
                  />
                  <span className={`text-sm ${product.stock > 0 ? "text-success" : "text-error"}`}>
                    {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
                  </span>
                </div>

                {/* Quantity Selector */}
                {product.stock > 0 && (
                  <div className="flex items-center space-x-4">
                    <span className="font-body-semibold text-text-primary">Quantity:</span>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                        disabled={selectedQuantity <= 1}
                        className="p-2"
                        iconName="Minus"
                        iconSize={16}
                      />
                      <span className="font-data font-data-medium text-lg w-12 text-center">
                        {selectedQuantity}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setSelectedQuantity(Math.min(product.stock, selectedQuantity + 1))}
                        disabled={selectedQuantity >= product.stock}
                        className="p-2"
                        iconName="Plus"
                        iconSize={16}
                      />
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    variant="primary"
                    onClick={handleAddToCart}
                    disabled={product.stock === 0 || isLoading}
                    loading={isLoading}
                    className="flex-1"
                    iconName="ShoppingCart"
                    iconSize={16}
                  >
                    Add to Cart
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleViewDetails}
                    className="flex-1"
                    iconName="Eye"
                    iconSize={16}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuickViewModal;